import { ASK } from '@/james-playground/aa-family-claim/claim/asks';
import { text, type Facts } from '@/james-playground/aa-family-claim/claim/facts';

/**
 * The words on the form, drafted from what the family said (spec 10).
 *
 * A draft is only ever the family's own quotes, tidied into sentences: every
 * sentence comes from a quote on an answer in that activity, nothing is added,
 * and a box with no quotes behind it has no draft. The family approves or
 * edits each one in "Make it stronger".
 */

export type Draft = { text: string; quotes: string[] };

/** Each "tell us more" box, and the ask whose answers it draws on. */
export const BOX_SOURCES: Record<string, { tellUsMore: string; asks: string[] }> = {
  bedHelp: { tellUsMore: 'bedTellUsMore', asks: ['day-bed'] },
  toilet: { tellUsMore: 'toiletTellUsMore', asks: ['day-toilet'] },
  washing: { tellUsMore: 'washingTellUsMore', asks: ['day-washing'] },
  dressing: { tellUsMore: 'dressingTellUsMore', asks: ['day-dressing'] },
  movingIndoors: { tellUsMore: 'movingIndoorsTellUsMore', asks: ['day-moving', 'day-falls'] },
  eating: { tellUsMore: 'eatingTellUsMore', asks: ['day-eating'] },
  medicationHelp: { tellUsMore: 'medicationTellUsMore', asks: ['day-medicines'] },
  communication: { tellUsMore: 'communicationTellUsMore', asks: ['day-communication'] },
  supervisionDay: { tellUsMore: 'supervisionDayTellUsMore', asks: ['safe-watch'] },
  nightHelp: { tellUsMore: 'nightHelpTellUsMore', asks: ['night-help'] },
  nightSupervision: { tellUsMore: 'nightSupervisionTellUsMore', asks: ['night-watch'] },
};

const sentence = (quote: string) => {
  // A quote cut mid-sentence ends in a comma: end it properly.
  const trimmed = quote.trim().replace(/\s+/g, ' ').replace(/[,;:]+$/, '');
  if (!trimmed) return '';
  const capital = trimmed[0].toUpperCase() + trimmed.slice(1);
  return /[.!?]$/.test(capital) ? capital : `${capital}.`;
};

const quotesFrom = (facts: Facts, askIds: string[]) => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const askId of askIds)
    for (const id of ASK[askId]?.entries ?? []) {
      const quote = facts[id]?.quote?.trim();
      if (quote && facts[id].state !== 'skipped' && !seen.has(quote)) {
        seen.add(quote);
        out.push(quote);
      }
    }
  return out;
};

const VARIABILITY = [
  { area: 'Help in the day', choice: 'variabilityDay', often: 'variabilityDayOften' },
  { area: 'Keeping her safe', choice: 'variabilitySupervision', often: 'variabilitySupervisionOften' },
  { area: 'Help at night', choice: 'variabilityNight', often: 'variabilityNightOften' },
];

/** The draft for one box, or null when nothing the family said covers it. */
export const draftFor = (boxId: string, facts: Facts): Draft | null => {
  if (boxId === 'variability') {
    // Q46: how needs change from day to day, from the good-and-bad-days answers.
    const lines = VARIABILITY.flatMap(({ area, choice, often }) => {
      const worse = text(facts, choice);
      if (!worse || worse === 'About the same every day') return [];
      const how = text(facts, often);
      return [`${area}: ${worse.toLowerCase()}${how ? `, ${how}` : ''}.`];
    });
    return lines.length ? { text: lines.join(' '), quotes: lines } : null;
  }
  const source = BOX_SOURCES[boxId];
  if (!source) return null;
  const quotes = quotesFrom(facts, source.asks);
  if (!quotes.length) return null;
  return { text: quotes.map(sentence).filter(Boolean).join(' '), quotes };
};

/** Every box with a draft, for the "words on the form" review. */
export const allDrafts = (facts: Facts): Record<string, Draft> =>
  Object.fromEntries(
    [...Object.keys(BOX_SOURCES), 'variability']
      .map((id) => [id, draftFor(id, facts)] as const)
      .filter((pair): pair is readonly [string, Draft] => pair[1] !== null),
  );
